<template>
  <div v-if="totalCount > 0 || alwaysShow" class="mb-5">
    <div class="mb-3">
      <div class="d-flex align-items-center">
        <div class="d-flex align-items-center">
          <h4 class="me-3 font-serif">
            {{ title }}
          </h4>
          <span class="ms-auto d-flex align-items-center gap-2">
            <span
              class="badge rounded-pill section-volume-badge"
              :class="{ 'section-volume-badge--empty': totalVolume === 0 }"
            >
              {{ volumeLabel }}: {{ formatNumberWithSpaces(totalVolume) }} MWh
            </span>
          </span>
          <span class="text-muted small">({{ totalCount }})</span>
        </div>
        <slot name="header-action" />
      </div>
      <span class="text-muted small d-none d-md-inline">
        {{ description }}
      </span>
    </div>

    <div
      class="section-collapse"
      :class="{ 'section-collapse--open': !collapsed }"
    >
      <div class="section-collapse__inner">
        <div v-if="totalCount === 0" class="text-muted small ps-4 py-2">
          {{ emptyMessage ?? $t("portfolios.no_portfolios") }}
        </div>
        <template v-else>
          <slot />
          <button
            v-if="hiddenCount > 0 || showingHidden"
            class="btn btn-sm btn-link text-muted text-decoration-none p-0 mt-2"
            @click="$emit('showHidden')"
          >
            <FontAwesomeIcon
              icon="fas fa-chevron-down"
              class="me-1 toggle-chevron"
              :class="{ 'toggle-chevron--open': showingHidden }"
            />
            {{
              showingHidden
                ? $t("portfolios.hide_empty")
                : $t("portfolios.show_empty", { count: hiddenCount })
            }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { sumBy } from "lodash-es";
import { computed, ref } from "vue";
import type { PortfolioGroup } from "../types";

const props = withDefaults(
  defineProps<{
    title: string;
    description: string;
    volumeLabel: string;
    portfolios: PortfolioGroup[];
    allPortfolios: PortfolioGroup[];
    hiddenCount?: number;
    showingHidden?: boolean;
    alwaysShow?: boolean;
    emptyMessage?: string;
  }>(),
  {
    hiddenCount: 0,
    showingHidden: false,
    alwaysShow: false,
  }
);

defineEmits<{
  showHidden: [];
}>();

const collapsed = ref(false);

const totalVolume = computed(() => sumBy(props.allPortfolios, "total_volume"));
const totalCount = computed(() => props.allPortfolios.length);
</script>

<style scoped lang="scss">
.section-volume-badge {
  background-color: rgba(var(--phoenix-dark-rgb, 33, 37, 41), 0.08);
  color: var(--phoenix-body-color, #5e6a5a);
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.3em 0.7em;
}

.section-volume-badge--empty {
  opacity: 0.5;
}

.section-collapse {
  display: grid;
  grid-template-rows: 1fr;
  opacity: 1;
  transition:
    grid-template-rows 150ms cubic-bezier(0.25, 0, 0.3, 1),
    opacity 100ms cubic-bezier(0.25, 0, 0.3, 1);
}

.section-collapse--open > .section-collapse__inner {
  overflow: visible;
}

.section-collapse:not(.section-collapse--open) {
  grid-template-rows: 0fr;
  opacity: 0;
}

.section-collapse__inner {
  overflow: hidden;
}

.toggle-chevron {
  transition: transform 150ms cubic-bezier(0.25, 0, 0.3, 1);
}

.toggle-chevron--open {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .section-collapse,
  .toggle-chevron {
    transition: none;
  }
}
</style>
